import { isEmpty } from 'lodash-es';
import { Area, IViolation, TaskListCharacteristic } from 'src/app/models';

/**
 * Validates that if an M500/Md500 code is present then the values are non-zero,
 * and that only the correct family of codes is used for the element type.
 *
 * @remarks
 * M500 codes are in the format `M500<uppercase alpha>+`, ie M500A..M500W and eventually M500AA, etc
 * Also note that an M500 code will always be suffixed.
 *
 * Regular elements use `M500` codes; DUPLO elements use `Md500` codes. Using a code from the
 * opposite family is itself a violation. Note `/^M500/` and `/^Md500/` are mutually exclusive
 * (`Md500` is `M` then `d`, not `M` then `5`).
 */
export abstract class MCode500Validation {
  public static validate(characteristics: TaskListCharacteristic[], isDuplo = false): IViolation[] {
    const expectedFamily = isDuplo ? /^Md500[A-Z]+/u : /^M500[A-Z]+/u;
    const wrongFamily = isDuplo ? /^M500[A-Z]+/u : /^Md500[A-Z]+/u;

    const emptyValueViolations = characteristics
      .filter((characteristic) => characteristic.mValidationCode.search(expectedFamily) !== -1)
      .map((characteristic) => ({
        characteristic,
        missingValues: MCode500Validation.emptyValues(characteristic),
      }))
      .filter(({ missingValues }) => !isEmpty(missingValues))
      .map(({ characteristic, missingValues }) => ({
        message: `Some values are missing: ${missingValues
          .map((missing) => `${missing}`)
          .join(', ')}`,
        pathToViolation: MCode500Validation.pathToViolation(characteristic),
      }));

    const wrongFamilyViolations = characteristics
      .filter((characteristic) => characteristic.mValidationCode.search(wrongFamily) !== -1)
      .map((characteristic) => ({
        message: `${characteristic.mValidationCode} is not allowed on a ${
          isDuplo ? 'DUPLO' : 'regular'
        } element (expected ${isDuplo ? 'Md500' : 'M500'} codes)`,
        pathToViolation: MCode500Validation.pathToViolation(characteristic),
      }));

    return emptyValueViolations.concat(wrongFamilyViolations);
  }

  private static pathToViolation(characteristic: TaskListCharacteristic) {
    return [
      { area: Area.Specifications },
      {
        area: Area.TechnicalSpecification,
        displayId: `${characteristic.taskListGroup}-${characteristic.groupCounter}`,
        id: `Q-${characteristic.taskListGroup}-${characteristic.groupCounter}`,
      },

      {
        area: Area.TechnicalSpecificationOperation,
        id: characteristic.infoField2,
      },
      {
        area: Area.TechnicalSpecificationCharacteristics,
        displayId: characteristic.characteristic,
        id: characteristic.id,
      },
    ];
  }

  // For M500 codes the following values always appear to be empty, and since these values are not
  // essential, we ignore them when doing the check.
  // Otherwise it would always (as it did previously) show validation errors on M500 codes.
  private static readonly propertyIgnoreList: Array<keyof TaskListCharacteristic> = [
    'inspectionMethodPlant',
    'pmtVersion',
    'refMasterInspectionCharacteristic',
    'longTextExists',
  ];

  // The tolerance properties should be considered empty when the value is 0.
  private static readonly toleranceProperties: Array<keyof TaskListCharacteristic> = [
    'targetValue',
    'upperLimit',
    'lowerLimit',
    'firstUpperLimit',
    'firstLowerLimit',
  ];

  private static emptyValues(characteristic: TaskListCharacteristic) {
    return Object.entries(characteristic)
      .filter(([key, value]) => {
        // Ignored properties are not empty
        if (this.propertyIgnoreList.some((x) => x === key)) {
          return false;
        }

        // Tolerance properties are empty when the value is 0
        if (this.toleranceProperties.some((x) => x === key) && Number(value) === 0) {
          return true;
        }

        // Number properties are not empty when the value is 0
        if (value === 0) {
          return false;
        }

        // Other properties are empty when falsy
        return !value;
      })
      .map(([key]) => key);
  }
}
